import { useCallback, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Snackbar,
} from '@mui/material';

import { gql } from '@graphql/__generated__';
import {
  type DevelopmentEnquiryResponseEmailFragment,
  type EmailTemplateFormLotSnippetDataFragment,
  type ListingEnquiryResponseEmailFragment,
} from '@graphql/__generated__/graphql';
import { ConfirmDialog } from '@modules/_shared/components/ConfirmDialog';
import {
  EmailTemplateForm,
  type EmailTemplateInput,
} from '@modules/_shared/components/EmailTemplateForm';
import {
  CopyRecipientsTrackingHint,
  hasCopyRecipients,
} from '@modules/_shared/components/listing-marketing/CopyRecipientsTrackingHint';
import {
  EmailSenderStatusAlert,
  hasEmailSendingAccount,
} from '@modules/_shared/components/listing-marketing/EmailSenderStatusAlert';
import { getEnquiryResponseEmailTemplate } from '@modules/_shared/components/listing-marketing/enquiryResponseEmailTemplate';
import { ENQUIRY_RESPONSE_ANCHORS } from '@modules/_shared/components/listing-marketing/enquiryResponseTour';
import { useEmailSender } from '@modules/_shared/components/listing-marketing/useEmailSender';
import { LoadingSwitch } from '@modules/_shared/components/LoadingSwitch';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { RaLink } from '@modules/_shared/components/RaLink';
import { useEnquiryResponseEmailTemplate } from '@modules/_shared/utils/enquiryResponseEmailTemplate';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { useLocale } from '@shared/hooks/locale';

type EnquiryResponseEmailFormProps = {
  onEnquiryResponseEnabledChange: (enabled: boolean) => Promise<boolean>;
  onSubmit: (input: EmailTemplateInput) => Promise<{ success: boolean }>;
  readOnly?: boolean;
} & (
  | {
      listing: ListingEnquiryResponseEmailFragment;
      isFromDevelopment?: boolean;
      lotSnippetData: EmailTemplateFormLotSnippetDataFragment;
      mode: 'listing';
    }
  | {
      development: DevelopmentEnquiryResponseEmailFragment;
      onDevelopmentOverrideChange: (enabled: boolean) => Promise<boolean>;
      isDevelopmentOverrideEnabled: boolean;
      mode: 'development';
    }
);

export const LISTING_ENQUIRY_RESPONSE_EMAIL_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingEnquiryResponseEmail on lots {
    id
    enquiry_response_enabled
    enquiry_response_email_templates: email_templates(
      where: { type: { _eq: "enquiry_auto_response" } }
      order_by: { updated_at: desc_nulls_last }
      limit: 1
    ) {
      ...EmailTemplateForm
    }
    response_sender {
      ...EmailSender
    }
    broker {
      ...EmailSender
    }
    ...EmailTemplateFormListing
    development {
      id
      enquiry_response_override_lot
      enquiry_response_enabled
      email_templates(
        where: { type: { _eq: "enquiry_auto_response" } }
        order_by: { updated_at: desc_nulls_last }
        limit: 1
      ) {
        ...EmailTemplateForm
      }
    }
  }
`);

export const DEVELOPMENT_ENQUIRY_RESPONSE_EMAIL_FRAGMENT = gql(/* GraphQL */ `
  fragment DevelopmentEnquiryResponseEmail on developments {
    id
    enquiry_response_enabled
    enquiry_response_email_templates: email_templates(
      where: { type: { _eq: "enquiry_auto_response" } }
      order_by: { updated_at: desc_nulls_last }
      limit: 1
    ) {
      ...EmailTemplateForm
    }
    response_sender {
      ...EmailSender
    }
    broker {
      ...EmailSender
    }
  }
`);

export const EnquiryResponseEmailForm: React.FC<
  EnquiryResponseEmailFormProps
> = props => {
  const { t, language } = useLocale();

  const { onEnquiryResponseEnabledChange, onSubmit, readOnly = false } = props;

  const isFromDevelopment =
    props.mode === 'listing' ? props.isFromDevelopment : true;
  const lotSnippetData = props.mode === 'listing' ? props.lotSnippetData : null;
  const onDevelopmentOverrideChange =
    props.mode === 'development'
      ? props.onDevelopmentOverrideChange
      : undefined;
  const isDevelopmentOverrideEnabled =
    props.mode === 'development' ? props.isDevelopmentOverrideEnabled : true;
  const data =
    props.mode === 'listing'
      ? props.listing
      : { ...props.development, development: null };

  // Partial development data from listing (for override check only)
  const listingDevelopment = data.development;
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [showSuccessUpdateMessage, setShowSuccessUpdateMessage] =
    useState(false);

  const initialSender = data.response_sender ?? data.broker ?? null;

  const { sender: responseSender, selectSender } =
    useEmailSender(initialSender);

  const canSendEmails = hasEmailSendingAccount(responseSender);

  // Check if development overrides (in case of listing)
  const isOverride = listingDevelopment?.enquiry_response_override_lot === true;

  // Get the appropriate email template
  const enquiryResponseTemplate = getEnquiryResponseEmailTemplate({
    development: listingDevelopment,
    listingTemplates: data.enquiry_response_email_templates,
  });

  const canEnableSending =
    enquiryResponseTemplate != null && responseSender != null;

  const enquiryResponseEnabled = data.enquiry_response_enabled ?? false;

  // Content is disabled in development mode when override is off, or in readOnly
  const contentDisabled = readOnly || !isDevelopmentOverrideEnabled;

  // Developments span several lots, so they get no default.
  const {
    emailTemplate: defaultEmailTemplate,
    loading: isLoadingDefaultEmailTemplate,
    error: defaultEmailTemplateError,
  } = useEnquiryResponseEmailTemplate({
    listing: props.mode === 'listing' ? props.listing : null,
    language,
    t,
    skip: enquiryResponseTemplate != null || isOverride || contentDisabled,
  });

  const [showCopyRecipientsHint, setShowCopyRecipientsHint] = useState(() =>
    hasCopyRecipients(
      enquiryResponseTemplate?.cc,
      enquiryResponseTemplate?.bcc,
    ),
  );

  const handleSubmit = useCallback(
    async (input: EmailTemplateInput) => {
      const { success } = await onSubmit(input);
      if (success) {
        setShowSuccessUpdateMessage(true);

        if (canSendEmails && !enquiryResponseEnabled) {
          setConfirmDialog(true);
        }
      }
    },
    [onSubmit, canSendEmails, enquiryResponseEnabled],
  );

  useAutoTour('enquiry-response', props.mode === 'listing' && !readOnly);

  return (
    <>
      <MutationErrorSnackbar error={defaultEmailTemplateError ?? null} />
      <Box
        sx={{
          pt: 1,
          position: 'relative',
        }}
      >
        {/* Override alert for listing mode when development overrides */}
        {isOverride && (
          <Box
            sx={{
              position: 'absolute',
              borderRadius: 1,
              top: -5,
              right: -5,
              left: -5,
              bottom: -5,
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'center',
            }}
          >
            <Alert
              severity="warning"
              sx={{
                mt: '30%',
                maxWidth: '90%',
                alignItems: 'center',
                '& .MuiAlert-action': {
                  pt: 0,
                },
              }}
              action={
                <Button
                  component={RaLink}
                  to={`/developments/${listingDevelopment.id}/marketing/`}
                  preserveSearchParams={isFromDevelopment}
                >
                  {t('Go to development')}
                </Button>
              }
            >
              {t('Enquiry response is handled by development')}
            </Alert>
          </Box>
        )}

        {/* Override toggle for development mode */}
        {onDevelopmentOverrideChange != null && (
          <>
            <FormControlLabel
              disabled={readOnly}
              control={
                <LoadingSwitch
                  onClick={onDevelopmentOverrideChange}
                  checked={isDevelopmentOverrideEnabled}
                  disabled={readOnly}
                />
              }
              label={t('Override listings auto-response')}
            />
            <FormHelperText sx={{ mb: 2 }}>
              {t(
                'When enabled, all listings in this development will use this email template instead of their own',
              )}
            </FormHelperText>
          </>
        )}

        {/* Main content - disabled when not applicable */}
        <Box
          sx={{
            opacity: contentDisabled ? 0.5 : 1,
            pointerEvents: contentDisabled ? 'none' : 'initial',
          }}
        >
          <FormControlLabel
            {...tourAnchor(ENQUIRY_RESPONSE_ANCHORS.autoSend)}
            disabled={!canEnableSending || isOverride || contentDisabled}
            control={
              <LoadingSwitch
                onClick={onEnquiryResponseEnabledChange}
                checked={enquiryResponseEnabled}
              />
            }
            label={t('Send email automatically')}
          />
          <FormHelperText>
            {!canEnableSending &&
              !isOverride &&
              !contentDisabled &&
              t('Please fill the template below to enable response')}
          </FormHelperText>
          <Box
            sx={{
              mt: 2,
              pointerEvents: isOverride ? 'none' : 'initial',
            }}
          >
            {!isOverride && !contentDisabled && (
              <>
                <EmailSenderStatusAlert sender={responseSender} />
                <CopyRecipientsTrackingHint show={showCopyRecipientsHint} />
              </>
            )}

            {isLoadingDefaultEmailTemplate ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box {...tourAnchor(ENQUIRY_RESPONSE_ANCHORS.template)}>
                <EmailTemplateForm
                  emailTemplate={
                    enquiryResponseTemplate ?? defaultEmailTemplate ?? undefined
                  }
                  disabled={isOverride || contentDisabled}
                  isTemplateShareable={false}
                  allowSubmitNotDirty={
                    enquiryResponseTemplate == null &&
                    defaultEmailTemplate != null
                  }
                  fields={['cc', 'bcc', 'from']}
                  initialSender={initialSender}
                  lotSnippetData={lotSnippetData ?? undefined}
                  developmentId={
                    props.mode === 'development'
                      ? props.development.id
                      : undefined
                  }
                  onChange={async (data, name, type) => {
                    setShowCopyRecipientsHint(
                      hasCopyRecipients(data?.cc, data?.bcc),
                    );
                    if (type === 'change' && name === 'from') {
                      await selectSender(data?.from);
                    }
                  }}
                  onSubmit={handleSubmit}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={showSuccessUpdateMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={5000}
        onClose={() => setShowSuccessUpdateMessage(false)}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {t('Your enquiry response email settings have been updated!')}
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={confirmDialog}
        title={t('enquiryResponseIsNotEnabled')}
        okText={t('enable')}
        cancelText={t('later')}
        onClose={() => setConfirmDialog(false)}
        onSubmit={async () => {
          await onEnquiryResponseEnabledChange(true);

          setConfirmDialog(false);
        }}
      />
    </>
  );
};
